import { useCallback, useMemo, useContext } from 'react';
import { RenderIf } from 'react-rainbow-components';
import { useOpenModal } from '@rainbow-modules/hooks';
import ListTile from 'components/ListTile';
import PhoneIcon from 'components/icons/phone';
import SmsIcon from 'components/icons/sms';
import FacebookSolid from 'components/icons/facebookSolid';
import InstagramSolid from 'components/icons/instagramSolid';
import Email from 'components/icons/email';
import resolveChannel from 'components/ThreadsList/helpers/resolveChannel';
import { ChannelType } from 'components/ChannelPicker/types';
import useSMSChat from 'hooks/useSMSChat';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import { Customer } from 'data/firestore/agent/customer/types';
import { MetaAdReferral } from 'data/firestore/globals';
import { PaidAdChip } from 'components/PaidAd';
import useCustomerPanel from 'hooks/useCustomerPanel';
import context, { Context } from '../../context';
import { CUSTOMER_CALL_MODAL } from '../../../../constants';
import {
    CustomerInfoContainer,
    QuickActions,
    StyledLink,
    StyledBadgeOverlay,
    StyledUserAvatar,
    StyledCustomerOpportunitiesBadge,
    TitleContainer,
    CustomerName,
    StyledButtonIcon,
} from './styled';

interface CustomerInfoProps {
    customer?: Customer | null | undefined;
    channel?: string;
    paidReferral?: MetaAdReferral | null;
}

const CustomerInfo = ({ customer, channel, paidReferral }: CustomerInfoProps) => {
    const { websiteChatbots = [] } = useContext<Context>(context);
    const [openCustomerCallModal] = useOpenModal(CUSTOMER_CALL_MODAL);
    const { openCustomerPanel } = useCustomerPanel();
    const { openChat } = useSMSChat();

    const customerChannel = customer?.channel;
    const threadChannel = resolveChannel({ channel: channel || '', customer: customer as Customer });

    const customerDisplayName = useMemo(
        () => getDisplayName(customer, formatIntlPhoneNumber(customer?.phoneNumber as string)),
        [customer],
    );

    const subtitle = useMemo(() => {
        if (!threadChannel || !customer) return '';

        switch (threadChannel) {
            case 'whatsapp':
                return customer.phoneNumber ? formatIntlPhoneNumber(customer.phoneNumber) : '';
            case 'facebook':
            case 'instagram':
            case 'tiktok':
            case 'telegram':
                return '';
            case 'widget_chatbot':
                return websiteChatbots.find((chatbot) => chatbot.id === channel?.split('.').at(1))?.name || '';
            default:
                return customerChannel;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerChannel, customer, websiteChatbots, threadChannel]);

    const handleCallClick = useCallback(() => {
        if (customer) {
            openCustomerCallModal({ customer });
        }
    }, [openCustomerCallModal, customer]);

    const handleSMSClick = useCallback(() => {
        if (customer) {
            openChat({ ...customer });
        }
    }, [openChat, customer]);

    const handleOpenCustomerPanel = useCallback(
        () => openCustomerPanel({ data: customer }),
        [customer, openCustomerPanel],
    );

    const customerId = customer?.id || '';

    return (
        <>
            <CustomerInfoContainer>
                <ListTile
                    icon={(
                        <StyledUserAvatar
                            size="large"
                            initials={getInitials(customer as unknown as Record<string, unknown>)}
                            src={customer?.photoURL}
                            value={customer?.id}
                            channel={customerChannel as ChannelType}
                        />
                    )}
                    title={(
                        <TitleContainer>
                            <CustomerName type="button" onClick={handleOpenCustomerPanel}>
                                {customerDisplayName}
                            </CustomerName>
                            <RenderIf isTrue={!!customerId}>
                                <StyledCustomerOpportunitiesBadge
                                    customersIds={[customerId]}
                                    allowCreate
                                />
                            </RenderIf>
                            <RenderIf isTrue={Boolean(paidReferral)}>
                                <PaidAdChip referral={paidReferral as MetaAdReferral} />
                            </RenderIf>
                        </TitleContainer>
                    )}
                    subtitle={subtitle}
                />
            </CustomerInfoContainer>
            <QuickActions>
                <RenderIf isTrue={customer?.phoneNumber}>
                    <StyledButtonIcon
                        icon={<PhoneIcon />}
                        onClick={handleCallClick}
                    />
                    <StyledButtonIcon
                        icon={(
                            <StyledBadgeOverlay
                                overlap="circle"
                                isHidden={!customer?.hasNewMessage}
                            >
                                <SmsIcon />
                            </StyledBadgeOverlay>
                        )}
                        onClick={handleSMSClick}
                    />
                    <RenderIf isTrue={customer?.email}>
                        <StyledLink as="a" href={`mailto:${customer?.email}`}>
                            <Email />
                        </StyledLink>
                    </RenderIf>
                </RenderIf>
                <RenderIf isTrue={customer?.facebookProfileURL}>
                    <StyledLink to={String(customer?.facebookProfileURL)} target="_blank">
                        <FacebookSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={customer?.instagramProfileURL}>
                    <StyledLink to={String(customer?.instagramProfileURL)} target="_blank">
                        <InstagramSolid />
                    </StyledLink>
                </RenderIf>
            </QuickActions>
        </>
    );
};

export default CustomerInfo;
